/**
 * AuthFlowDialog
 *
 * Renders the signature dialog based on session state.
 * Placed once in MainLayout — visible whenever status === "authenticating".
 * Reuses the existing SignatureDialog component for the UI.
 */

import { useCallback, useMemo } from "react";
import { useBrowserWallet } from "@/features/wallet";
import {
  SignatureDialog,
  type AuthStep,
} from "@/features/auth/components/SignatureDialog";
import { useSessionManager } from "../session-manager";

function mapAuthSubStateToStep(authSubState: string | null): AuthStep {
  switch (authSubState) {
    case "challenging":
      return "loading";
    case "previewing":
      return "preview";
    case "signing":
    case "verifying":
      return "signing";
    case "error":
      return "error";
    default:
      return "idle";
  }
}

export function AuthFlowDialog() {
  const status = useSessionManager((s) => s.status);
  const authSubState = useSessionManager((s) => s.authSubState);
  const challengeMessage = useSessionManager((s) => s.challengeMessage);
  const walletAddress = useSessionManager((s) => s.walletAddress);
  const authError = useSessionManager((s) => s.authError);
  const confirmSign = useSessionManager((s) => s.confirmSign);
  const retry = useSessionManager((s) => s.retry);
  const cancel = useSessionManager((s) => s.cancel);

  // Wallet ops for the signing/cancel actions (provider-owned core).
  const wallet = useBrowserWallet();

  const isOpen = status === "authenticating";
  const step = useMemo(
    () => mapAuthSubStateToStep(authSubState),
    [authSubState],
  );
  const isLoading =
    authSubState === "challenging" ||
    authSubState === "signing" ||
    authSubState === "verifying";

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        cancel(wallet);
      }
    },
    [cancel, wallet],
  );

  const handleConfirm = useCallback(() => {
    confirmSign(wallet);
  }, [confirmSign, wallet]);

  const handleRetry = useCallback(() => {
    retry();
  }, [retry]);

  if (!isOpen) return null;

  return (
    <SignatureDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      step={step}
      challengeMessage={challengeMessage ?? ""}
      walletAddress={walletAddress ?? ""}
      error={authError}
      isLoading={isLoading}
      onConfirm={handleConfirm}
      onRetry={handleRetry}
    />
  );
}
